import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play } from "lucide-react";
import { SERIES, IMAGES } from "../data/content";
import { scrollToId } from "../lib/scroll";

export const Hero = ({ start = true }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "38%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

    return (
        <section id="top" ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[680px] overflow-hidden">
            <motion.div style={{ y: bgY }} className="absolute inset-0">
                <div
                    className="absolute inset-0 animate-kenburns bg-cover bg-center"
                    style={{ backgroundImage: `url(${IMAGES.hero})` }}
                />
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-b from-[#04060d]/70 via-[#04060d]/35 to-[#04060d]" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#04060d]/85 via-transparent to-[#04060d]/40" />
            <div className="absolute -left-40 top-1/3 h-[560px] w-[560px] rounded-full bg-[#d6c68e]/20 blur-[160px] animate-pulse-red" />

            <motion.div
                style={{ y: contentY, opacity: fade }}
                className="relative z-10 flex h-full flex-col justify-end mx-auto max-w-[1500px] px-5 sm:px-8 pb-14 sm:pb-20"
            >
                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 16 }}
                    transition={{ delay: 0.25, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    className="font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.4em] text-[#d6c68e] mb-5"
                >
                    Celestial Light Originals · A Pilot Presentation
                </motion.p>

                <h1 data-testid="hero-title" className="sr-only">
                    Deadly Ambition
                </h1>
                <div className="overflow-hidden">
                    <motion.img
                        src="/brand/title-metallic.png"
                        alt="Deadly Ambition"
                        initial={{ y: "38%", opacity: 0 }}
                        animate={start ? { y: 0, opacity: 1 } : { y: "38%", opacity: 0 }}
                        transition={{ delay: 0.45, duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                        className="w-[min(88vw,780px)] h-auto"
                    />
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={start ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
                    transition={{ delay: 1.05, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    className="mt-8 flex flex-col gap-7 md:flex-row md:items-end md:justify-between"
                >
                    <p data-testid="hero-logline" className="max-w-xl font-serif text-base sm:text-lg italic text-zinc-300 leading-relaxed">
                        {SERIES.logline}
                    </p>
                    <div className="flex flex-wrap items-center gap-4">
                        <button
                            data-testid="hero-watch-teaser-btn"
                            onClick={() => scrollToId("#teaser")}
                            className="group flex items-center gap-3 bg-[#d6c68e] px-7 py-3.5 font-mono text-[11px] tracking-[0.25em] text-[#04060d] transition-all duration-300 hover:bg-[#f2e9c8] hover:shadow-[0_0_36px_rgba(214,198,142,0.5)]"
                        >
                            <Play size={14} className="fill-current transition-transform duration-300 group-hover:scale-125" />
                            WATCH THE TEASER
                        </button>
                        <button
                            data-testid="hero-request-deck-btn"
                            onClick={() => scrollToId("#contact")}
                            className="border border-white/25 px-7 py-3.5 font-mono text-[11px] tracking-[0.25em] text-white transition-all duration-300 hover:border-[#d6c68e] hover:text-[#d6c68e]"
                        >
                            REQUEST THE DECK
                        </button>
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={start ? { opacity: 1 } : { opacity: 0 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="mt-9 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-5"
                    data-testid="hero-meta-strip"
                >
                    {SERIES.meta.map((m) => (
                        <span key={m} className="font-mono text-[10px] tracking-[0.28em] text-zinc-500">
                            {m}
                        </span>
                    ))}
                </motion.div>
            </motion.div>

            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 hidden sm:flex flex-col items-center gap-2">
                <span className="h-8 w-px bg-white/25 overflow-hidden relative">
                    <span className="absolute top-0 h-3 w-px bg-[#d6c68e] animate-scroll-cue" />
                </span>
            </div>
        </section>
    );
};
